import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Table, ActionIcon, Menu, Modal, TextInput } from '@mantine/core';
import { IconPlus, IconDotsVertical, IconEdit, IconTrash } from '@tabler/icons-react';
import { useEmailTemplates, useDeleteEmailTemplate, useCreateEmailTemplate } from '@/hooks/api-hooks';
import { EMAIL_TEMPLATES_PATH } from '@/lib/navigation';
import { openConfirmModal } from '@mantine/modals';
import dayjs from 'dayjs';
import { SkeletonList } from '@/components/snippets';


export default function EmailTemplatesPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        data: templatesResponse,
        isLoading
    } = useEmailTemplates();
    const templates = templatesResponse?.data || [];
    const deleteTemplate = useDeleteEmailTemplate();
    const createTemplateMutation = useCreateEmailTemplate();

    const [createModalOpened, setCreateModalOpened] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');

    const handleCreateTemplate = () => {
        if (!newTemplateName.trim()) return;
        createTemplateMutation.mutate(
            { name: newTemplateName },
            {
                onSuccess: (res) => {
                    setCreateModalOpened(false);
                    setNewTemplateName('');
                    if (res?.data?.id) {
                        navigate(`${EMAIL_TEMPLATES_PATH}/${res.data.id}`);
                    }
                }
            }
        );
    };

    const handleDelete = (id: number) => {
        openConfirmModal({
            title: t('delete_template'),
            children: t('delete_template_confirm'),
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () => deleteTemplate.mutate(id),
        });
    };

    return (
        <div className="panel-content space-y-6">
            <Button
                size='xs'
                color='black'
                radius='xl'
                leftSection={<IconPlus size={16} />}
                onClick={() => setCreateModalOpened(true)}
            >
                {t('create_template')}
            </Button>
            <Modal
                opened={createModalOpened}
                onClose={() => setCreateModalOpened(false)}
                title={t('create_template')}
                centered
            >
                <div className="space-y-4">
                    <TextInput
                        label={t('template_name')}
                        placeholder={t('template_name_placeholder') || 'Enter template name...'}
                        value={newTemplateName}
                        onChange={(e) => setNewTemplateName(e.currentTarget.value)}
                        data-autofocus
                        required
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="default" onClick={() => setCreateModalOpened(false)}>
                            {t('cancel')}
                        </Button>
                        <Button
                            color="black"
                            onClick={handleCreateTemplate}
                            loading={createTemplateMutation.isPending}
                            disabled={!newTemplateName.trim()}
                        >
                            {t('save')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {isLoading && <SkeletonList />}

            {!isLoading && (
                <div className='subtle-table'>
                    <table>
                        <thead>
                            <tr>
                                <th>{t('name')}</th>
                                <th>{t('subject')}</th>
                                <th>{t('created_at')}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map((template) => (
                                <tr key={template.id}>
                                    <td className='font-bold'>{template.name}</td>
                                    <td>{template.subject}</td>
                                    <td>
                                        {dayjs(template.created_at).format('DD/MM/YYYY')}
                                    </td>
                                    <td>
                                        <Menu position="bottom-end" shadow="sm">
                                            <Menu.Target>
                                                <ActionIcon variant="subtle" color="gray">
                                                    <IconDotsVertical size={16} />
                                                </ActionIcon>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                <Menu.Item
                                                    leftSection={<IconEdit size={14} />}
                                                    onClick={() => navigate(`${EMAIL_TEMPLATES_PATH}/${template.id}`)}
                                                >
                                                    {t('edit')}
                                                </Menu.Item>
                                                <Menu.Item
                                                    color="red"
                                                    leftSection={<IconTrash size={14} />}
                                                    onClick={() => handleDelete(template.id)}
                                                >
                                                    {t('delete')}
                                                </Menu.Item>
                                            </Menu.Dropdown>
                                        </Menu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
